/**
 * MATCH_BUNDLE wizard — 3-step flow to create + publish a scored-match
 * bundle on the marketplace.
 *
 *   Step 1: Name + description + season + price (ZC)
 *   Step 2: Match picker — search-filter the scored-matches list,
 *           multi-select.
 *   Step 3: Review + Publish — POSTs the Pack, fans matches into PackMatch
 *           rows, then calls /publish/ which fires the promotion blast.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=MATCH_BUNDLE.
 * Surfaces the per-channel promotion result inline so the founder sees
 * exactly what went out.
 */
import { useEffect, useRef, useState } from "react";
import { X, ArrowLeft, ArrowRight, Check, Search } from "lucide-react";
import {
  createPack, addPackMatch, publishPack, listScoredMatches,
} from "../../api/owner.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";

const STEPS = ["Details", "Pick matches", "Review + publish"];

export default function MatchBundleWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  // 2026-06-08 — Esc / hardware-back / backdrop. Block while busy so
  // an in-flight pack-create doesn't get orphaned.
  const safeClose = busy ? () => {} : (onClose || (() => {}));
  useDialogDismiss({ open: true, onClose: safeClose, ref: panelRef });

  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 99,
  });

  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [publishResult, setPublishResult] = useState(null);
  // Default ON for safety — first-time use should NEVER accidentally
  // blast WhatsApp + email to every B2C subscriber.
  const [testMode, setTestMode] = useState(true);

  // Fetch matches when step 2 opens or search changes.
  useEffect(() => {
    if (step !== 1) return;
    let cancelled = false;
    setMatchesLoading(true);
    listScoredMatches({ search, page_size: 50 })
      .then(({ data }) => {
        if (!cancelled) setMatches(data.results || data);
      })
      .finally(() => { if (!cancelled) setMatchesLoading(false); });
    return () => { cancelled = true; };
  }, [step, search]);

  function toggleMatch(id) {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  async function handlePublish() {
    setBusy(true);
    setError(null);
    setPublishResult(null);
    try {
      // 1) Create the Pack.
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data: pack } = await createPack({
        name: details.name.trim(),
        slug,
        pack_type: "MATCH_BUNDLE",
        season: details.season.trim(),
        short_description: details.short_description.trim(),
        description: details.description.trim(),
        price_credits: Number(details.price_credits),
      });

      // 2) Attach each selected match.
      let position = 0;
      for (const matchId of selectedIds) {
        await addPackMatch(pack.id, matchId, position++);
      }

      // 3) Publish — when ``testMode`` is on, the backend skips the
      // WhatsApp / email / in-app fan-out (the pack still goes live,
      // it just doesn't notify subscribers).
      const { data: published } = await publishPack(pack.id, {
        skipPromotion: testMode,
      });
      setPublishResult({
        pack_name: published.name,
        pack_id: pack.id,
        matches: selectedIds.size,
        promotion: published.promotion || [],
        test_mode: testMode,
      });
      if (onCreated) onCreated(pack);
    } catch (e) {
      const msg = e?.response?.data
        ? (typeof e.response.data === "string"
            ? e.response.data
            : JSON.stringify(e.response.data))
        : e.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="New scored-match bundle"
        className="bg-bg-primary text-text-primary border border-border-default rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-border-default flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-text-secondary">
              Marketplace · MATCH_BUNDLE
            </div>
            <div className="text-lg font-semibold">
              {publishResult ? "Published" : "New scored-match bundle"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-bg-secondary"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        {!publishResult && (
          <div className="px-5 py-3 border-b border-border-default flex items-center gap-2 text-xs">
            {STEPS.map((label, i) => (
              <span key={label} className="flex items-center gap-2">
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center font-semibold ${
                    i < step
                      ? "bg-emerald-600 text-white"
                      : i === step
                        ? "bg-indigo-600 text-white"
                        : "bg-bg-secondary text-text-secondary"
                  }`}
                >
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span
                  className={i === step ? "font-semibold" : "text-text-secondary"}
                >
                  {label}
                </span>
                {i < STEPS.length - 1 && (
                  <span className="text-text-secondary mx-1">→</span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm">
              {error}
            </div>
          )}

          {publishResult ? (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 p-3 text-sm">
                <div className="font-semibold">
                  "{publishResult.pack_name}" published.
                </div>
                <div className="text-xs mt-1 opacity-80">
                  {publishResult.matches} match{publishResult.matches === 1 ? "" : "es"} bundled.
                  {publishResult.test_mode && " · Test mode — no subscriber blast was sent."}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-text-secondary mb-2">
                  Promotion fan-out {publishResult.test_mode && "(skipped — test mode)"}
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-table-header text-[11px] uppercase text-text-secondary">
                    <tr>
                      <th className="text-left px-2 py-1">Channel</th>
                      <th className="text-left px-2 py-1">Status</th>
                      <th className="text-right px-2 py-1">Recipients</th>
                      <th className="text-left px-2 py-1">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishResult.promotion.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-3 text-text-secondary text-center">
                        No promotion rows returned.
                      </td></tr>
                    )}
                    {publishResult.promotion.map((p, i) => (
                      <tr key={i} className="border-t border-border-default">
                        <td className="px-2 py-1">{p.channel}</td>
                        <td className="px-2 py-1">
                          <span className={
                            p.status === "sent" ? "text-emerald-300"
                            : p.status === "skipped" ? "text-text-secondary"
                            : "text-red-300"
                          }>{p.status}</span>
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{p.recipients}</td>
                        <td className="px-2 py-1 text-xs text-text-secondary">{p.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : step === 0 ? (
            <div className="space-y-3">
              <Field label="Pack name" required>
                <input
                  type="text"
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  placeholder="e.g. IPL 2026 — Playoffs (15 matches)"
                  className="w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </Field>
              <Field label="Season">
                <input
                  type="text"
                  value={details.season}
                  onChange={(e) => setDetails({ ...details, season: e.target.value })}
                  placeholder="2026"
                  className="w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </Field>
              <Field label="Short description (≤ 280 chars)">
                <input
                  type="text"
                  maxLength={280}
                  value={details.short_description}
                  onChange={(e) => setDetails({ ...details, short_description: e.target.value })}
                  placeholder="Marketplace tile copy."
                  className="w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={details.description}
                  onChange={(e) => setDetails({ ...details, description: e.target.value })}
                  rows={3}
                  placeholder="Detail page copy."
                  className="w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </Field>
              <Field label="Price (ZenCredits)" required>
                <input
                  type="number"
                  min={0}
                  value={details.price_credits}
                  onChange={(e) => setDetails({ ...details, price_credits: e.target.value })}
                  className="w-full px-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </Field>
            </div>
          ) : step === 1 ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by team or stadium…"
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-bg-secondary border border-border-default text-sm"
                />
              </div>
              <div className="text-xs text-text-secondary">
                Only matches with innings present (i.e. actually scored) are listed.
                {selectedIds.size > 0 && (
                  <span className="ml-2 text-emerald-400">
                    {selectedIds.size} selected
                  </span>
                )}
              </div>
              <div className="rounded-md border border-border-default overflow-hidden">
                {matchesLoading && matches.length === 0 && (
                  <div className="p-4 text-center text-text-secondary text-sm">Loading…</div>
                )}
                {!matchesLoading && matches.length === 0 && (
                  <div className="p-4 text-center text-text-secondary text-sm">
                    No scored matches found.
                  </div>
                )}
                {matches.map((m) => {
                  const checked = selectedIds.has(m.id);
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-border-default last:border-b-0 ${
                        checked ? "bg-emerald-500/10" : "hover:bg-bg-secondary"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMatch(m.id)}
                        className="shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.label}</div>
                        <div className="text-xs text-text-secondary truncate">
                          {m.match_date || "?"} · {m.stadium || "—"} · {m.format || "—"} · {m.innings_count} inn.
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <ReviewRow label="Name" value={details.name} />
              <ReviewRow label="Season" value={details.season || "—"} />
              <ReviewRow label="Price" value={`${details.price_credits} ZC`} />
              <ReviewRow label="Matches in bundle" value={String(selectedIds.size)} />
              <ReviewRow label="Description" value={details.short_description || "—"} />

              <div className="border-t border-border-default pt-3 mt-3">
                <label className={`flex items-start gap-2 cursor-pointer rounded-md p-3 border ${
                  testMode
                    ? "border-amber-500/40 bg-amber-500/10"
                    : "border-red-500/40 bg-red-500/10"
                }`}>
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    className="mt-0.5 shrink-0"
                  />
                  <div className="text-sm">
                    <div className="font-semibold">
                      {testMode
                        ? "Test mode — DON'T blast subscribers"
                        : "LIVE — will message every B2C subscriber"}
                    </div>
                    <div className={`text-xs mt-1 ${testMode ? "text-amber-200" : "text-red-200"}`}>
                      {testMode
                        ? "Pack still publishes (visible on marketplace), but no WhatsApp / email / in-app notification is sent. Safe for first-time / dry-run."
                        : "Pack publishes AND triggers the fan-out: WhatsApp template + email blast + in-app banner. 6-hour dedupe per channel."}
                    </div>
                  </div>
                </label>
              </div>

              <div className="text-xs text-text-secondary pt-1">
                The pack lands at
                <code className="mx-1">/marketplace/{details.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}</code>.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!publishResult && (
          <div className="px-5 py-3 border-t border-border-default flex items-center justify-between gap-2">
            <button
              onClick={() => step > 0 ? setStep(step - 1) : onClose()}
              disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary disabled:opacity-50"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {step === 0 ? "Cancel" : "Back"}
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={busy
                  || (step === 0 && !detailsValid())
                  || (step === 1 && selectedIds.size === 0)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
              >
                Next
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={handlePublish}
                disabled={busy}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-white text-sm font-semibold disabled:opacity-50 ${
                  testMode
                    ? "bg-amber-600 hover:bg-amber-500"
                    : "bg-red-600 hover:bg-red-500"
                }`}
              >
                {busy
                  ? "Publishing…"
                  : testMode
                    ? "Publish (test mode — no blast)"
                    : "Publish + blast subscribers"}
              </button>
            )}
          </div>
        )}
        {publishResult && (
          <div className="px-5 py-3 border-t border-border-default flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-text-secondary block mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
    </label>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <div className="text-xs uppercase tracking-widest text-text-secondary w-32 shrink-0">
        {label}
      </div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
